import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Bell,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Search,
  Users,
  UsersRound,
  LogOut,
  List,
  MoreHorizontal,
  Trash2,
  Palette,
  Shield,
  ShieldAlert,
  MessageSquare,
  Tags,
  Archive,
  ArchiveRestore,
  PanelsTopLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useUIStore } from '@/stores/ui-store';
import { useProjects, useArchiveProject, useUnarchiveProject, useDeleteProject } from '@/hooks/use-projects';
import { useLists, useDeleteList, useArchiveList, useUnarchiveList } from '@/hooks/use-lists';
import { useAllChats } from '@/hooks/use-chats';
import { useAuth } from '@/hooks/use-auth';
import { hasAnyCapability, useMyCapabilities, type CapabilityMap } from '@/hooks/use-permissions';
import { cn, getInitials } from '@/lib/utils';
import { toast } from '@/components/ui/toaster';
import { ProjectStatusesDialog, ListStatusesDialog } from '@/features/statuses';
import { ProjectMembershipDialog, ListMembershipDialog } from '@/features/membership';
import { ListTagSetsDialog } from '@/components/lists/list-tag-sets-dialog';
import { ManageCustomFieldsDialog } from '@/features/custom-fields';
import { ProjectSettingsDialog, PROJECT_ICONS } from '@/components/projects/ProjectSettingsDialog';
import type { GlobalRole, List as ListType } from '@/types';

function apiErrorMessage(error: unknown, fallback: string): string {
  const e = error as { response?: { data?: { message?: string } } };
  return e.response?.data?.message ?? fallback;
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  href: string;
  allowedRoles?: GlobalRole[];
  /** Show if user has any of these capabilities (used instead of/in addition to allowedRoles for Tags) */
  anyOfCapabilities?: string[];
}

const navItems: NavItem[] = [
  {
    icon: PanelsTopLeft,
    label: 'Startportal',
    href: '/start',
    anyOfCapabilities: ['startportal.view'],
  },
  { icon: LayoutDashboard, label: 'Dashboard', href: '/dashboard' },
  { icon: FolderKanban, label: 'Projects', href: '/projects' },
  { icon: MessageSquare, label: 'Chats', href: '/company-chats', anyOfCapabilities: ['chat.company.view', 'chat.project.view'] },
  { icon: Search, label: 'Search', href: '/search' },
  { icon: Bell, label: 'Notifications', href: '/notifications' },
  { icon: Users, label: 'Users', href: '/users', allowedRoles: ['admin'] },
  { icon: UsersRound, label: 'Teams', href: '/teams', allowedRoles: ['admin', 'supervisor'] },
  {
    icon: Tags,
    label: 'Tags',
    href: '/tag-manager',
    anyOfCapabilities: ['tag_sets.create', 'tag_sets.edit', 'tags.create', 'tags.edit', 'list.tag_sets.assign'],
  },
  { icon: Shield, label: 'Permissions', href: '/permissions', anyOfCapabilities: ['permissions.manage'] },
  { icon: ShieldAlert, label: 'Security', href: '/security', anyOfCapabilities: ['security.view'] },
  { icon: Settings, label: 'Settings', href: '/settings' },
];

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sidebarCollapsed, toggleSidebarCollapsed, setCreateProjectOpen, setCreateListOpen } = useUIStore();
  const [showArchived, setShowArchivedState] = useState(() =>
    typeof window !== 'undefined' ? localStorage.getItem('weticket_show_archived') === 'true' : false
  );
  const setShowArchived = (v: boolean) => {
    setShowArchivedState(v);
    try {
      localStorage.setItem('weticket_show_archived', v ? 'true' : 'false');
    } catch { /* ignore */ }
  };
  const { data: projects } = useProjects(showArchived);
  const { data: allChats = [] } = useAllChats();
  const { user, logout } = useAuth();
  const { data: capabilities, isLoading: capabilitiesLoading } = useMyCapabilities();

  const userRole = user?.role;
  const isClient = userRole === 'client';
  const canArchiveProject = Boolean(capabilities?.['project.archive'] || capabilities?.['project.unarchive']);
  const canCreateProject = Boolean(capabilities?.['project.create']);

  const [sectionNavExpanded, setSectionNavExpandedState] = useState(() => {
    try {
      return localStorage.getItem('weticket_sidebar_section_nav') !== 'false';
    } catch {
      return true;
    }
  });
  const [sectionProjectsExpanded, setSectionProjectsExpandedState] = useState(() => {
    try {
      return localStorage.getItem('weticket_sidebar_section_projects') !== 'false';
    } catch {
      return true;
    }
  });
  const setSectionNavExpanded = (v: boolean) => {
    setSectionNavExpandedState(v);
    try {
      localStorage.setItem('weticket_sidebar_section_nav', v ? 'true' : 'false');
    } catch { /* ignore */ }
  };
  const setSectionProjectsExpanded = (v: boolean) => {
    setSectionProjectsExpandedState(v);
    try {
      localStorage.setItem('weticket_sidebar_section_projects', v ? 'true' : 'false');
    } catch { /* ignore */ }
  };
  
  // Calculate total unread chats count across all chats (company + project)
  const totalUnreadChats = allChats.reduce((sum, chat) => sum + (chat.unreadCount ?? 0), 0);

  // Track which projects are expanded
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  // Get current project ID from URL
  const currentProjectMatch = location.pathname.match(/\/projects\/([^/]+)/);
  const currentProjectId = currentProjectMatch?.[1];
  const currentListMatch = location.pathname.match(/\/projects\/[^/]+\/lists\/([^/?#]+)/);
  const currentListId = currentListMatch?.[1] ?? searchParams.get('listId');

  // Auto-expand current project
  useEffect(() => {
    if (currentProjectId) {
      setExpandedProjects((prev) => new Set(prev).add(currentProjectId));
    }
  }, [currentProjectId]);

  const toggleProjectExpanded = (projectId: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const filteredNavItems = navItems.filter((item) => {
    if (item.anyOfCapabilities?.length) {
      if (capabilitiesLoading) return false;
      return hasAnyCapability(capabilities, item.anyOfCapabilities);
    }
    if (!item.allowedRoles) return true;
    return userRole && item.allowedRoles.includes(userRole);
  });

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen flex-col border-r bg-card transition-all duration-300',
          sidebarCollapsed ? 'w-16' : 'w-72'
        )}
      >
        <div className="flex h-14 items-center justify-between border-b px-4">
          {!sidebarCollapsed && (
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">
                W
              </div>
              <span className="font-semibold">WeTicket</span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebarCollapsed}
            className={cn(sidebarCollapsed && 'mx-auto')}
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        <ScrollArea className="flex-1 py-4">
          {/* Main navigation section - collapsible */}
          {!sidebarCollapsed && (
            <div className="px-2 mb-1">
              <button
                type="button"
                onClick={() => setSectionNavExpanded(!sectionNavExpanded)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold uppercase text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground transition-colors"
              >
                <ChevronDown
                  className={cn('h-4 w-4 shrink-0 transition-transform', !sectionNavExpanded && '-rotate-90')}
                />
                <span>Navigation</span>
              </button>
            </div>
          )}
          {sectionNavExpanded && (
            <nav className="space-y-1 px-2">
              {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
              const Icon = item.icon;
              // Show badge for Chats nav item
              const badge = item.href === '/company-chats' && totalUnreadChats > 0 ? totalUnreadChats : null;

              if (sidebarCollapsed) {
                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.href}
                        className={cn(
                          'relative flex h-10 w-10 items-center justify-center rounded-lg mx-auto transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <Icon className="h-5 w-5" />
                        {badge !== null && (
                          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-semibold rounded-full bg-destructive text-destructive-foreground">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span className="flex-1">{item.label}</span>
                  {badge !== null && (
                    <span className="flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-semibold rounded-full bg-destructive text-destructive-foreground">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          )}

          <Separator className="my-4" />

          <div className="px-2">
            {!sidebarCollapsed && (
              <div className="mb-2 flex items-center justify-between px-3">
                <button
                  type="button"
                  onClick={() => setSectionProjectsExpanded(!sectionProjectsExpanded)}
                  className="flex items-center gap-1.5 rounded-md py-0.5 pr-1 text-xs font-semibold uppercase text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground transition-colors"
                >
                  <ChevronDown
                    className={cn('h-4 w-4 shrink-0 transition-transform', !sectionProjectsExpanded && '-rotate-90')}
                  />
                  <span>Projects</span>
                </button>
                <div className="flex items-center gap-0.5">
                  {canArchiveProject && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setShowArchived(!showArchived)}
                      title={showArchived ? 'Hide archived' : 'Show archived'}
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  )}
                {!isClient && canCreateProject && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setCreateProjectOpen(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
                </div>
              </div>
            )}

            {sectionProjectsExpanded && (
            <nav className="space-y-0.5">
              {projects?.slice(0, 15).map((project) => {
                const isProjectActive = location.pathname === `/projects/${project.id}`;
                const isExpanded = expandedProjects.has(project.id);

                if (sidebarCollapsed) {
                  return (
                    <Tooltip key={project.id}>
                      <TooltipTrigger asChild>
                        <Link
                          to={`/projects/${project.id}`}
                          className={cn(
                            'flex h-10 w-10 items-center justify-center rounded-lg mx-auto transition-colors',
                            isProjectActive
                              ? 'bg-accent text-accent-foreground'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          )}
                        >
                          <div
                            className="h-3 w-3 rounded-sm"
                            style={{ backgroundColor: project.color }}
                          />
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right">{project.name}</TooltipContent>
                    </Tooltip>
                  );
                }

                return (
                  <ProjectItem
                    key={project.id}
                    project={project}
                    capabilities={capabilities}
                    isActive={isProjectActive}
                    isExpanded={isExpanded}
                    currentListId={currentListId}
                    isClient={isClient}
                    showArchived={showArchived}
                    onToggleExpand={() => toggleProjectExpanded(project.id)}
                    onAddList={() => {
                      navigate(`/projects/${project.id}`);
                      setCreateListOpen(true, project.id);
                    }}
                  />
                );
              })}
            </nav>
            )}
          </div>
        </ScrollArea>

        <div className="border-t p-2">
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Avatar className="h-8 w-8 cursor-pointer">
                    <AvatarImage src={user?.avatarUrl || undefined} alt={user?.displayName} />
                    <AvatarFallback>{user ? getInitials(user.displayName) : '?'}</AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <div>
                    <p className="font-medium">{user?.displayName}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => logout()}>
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Sign out</TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-lg p-2">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatarUrl || undefined} alt={user?.displayName} />
                <AvatarFallback>{user ? getInitials(user.displayName) : '?'}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.displayName}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => logout()}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}

interface ProjectItemProps {
  project: { id: string; name: string; color: string; icon?: string | null; isArchived?: boolean; description?: string | null; createdBy?: string; createdAt?: string; updatedAt?: string };
  capabilities: CapabilityMap | undefined;
  isActive: boolean;
  isExpanded: boolean;
  currentListId: string | null;
  isClient: boolean;
  showArchived: boolean;
  onToggleExpand: () => void;
  onAddList: () => void;
}

function ProjectItem({
  project,
  capabilities,
  isActive,
  isExpanded,
  currentListId,
  isClient,
  showArchived,
  onToggleExpand,
  onAddList,
}: ProjectItemProps) {
  const navigate = useNavigate();
  const canArchiveProject = Boolean(capabilities?.['project.archive']);
  const canUnarchiveProject = Boolean(capabilities?.['project.unarchive']);
  const canDeleteProject = Boolean(capabilities?.['project.delete']);
  const canArchiveList = Boolean(capabilities?.['list.archive']);
  const canCreateList = Boolean(capabilities?.['list.create']);
  const canManageProjectMembers = Boolean(capabilities?.['project.members.manage']);
  const canManageProjectStatuses = Boolean(capabilities?.['status.project.manage']);
  const canManageProjectCustomFields = Boolean(capabilities?.['custom_fields.project.manage']);
  const [customFieldsDialogOpen, setCustomFieldsDialogOpen] = useState(false);
  const { data: lists = [] } = useLists(isExpanded ? project.id : undefined, showArchived);
  const deleteList = useDeleteList();
  const archiveProject = useArchiveProject();
  const unarchiveProject = useUnarchiveProject();
  const deleteProject = useDeleteProject();
  const archiveList = useArchiveList();
  const [deleteConfirmList, setDeleteConfirmList] = useState<ListType | null>(null);
  const [listDeleteConfirmed, setListDeleteConfirmed] = useState(false);
  const [archiveConfirmProject, setArchiveConfirmProject] = useState<typeof project | null>(null);
  const [deleteConfirmProject, setDeleteConfirmProject] = useState<typeof project | null>(null);
  const [projectDeleteConfirmed, setProjectDeleteConfirmed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [membershipDialogOpen, setMembershipDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const handleDeleteList = async () => {
    if (!deleteConfirmList || !listDeleteConfirmed) return;
    try {
      await deleteList.mutateAsync({
        id: deleteConfirmList.id,
        projectId: project.id,
      });
      toast({ title: 'List deleted permanently' });
      setDeleteConfirmList(null);
      setListDeleteConfirmed(false);
    } catch (error: unknown) {
      toast({
        title: 'Failed to delete list',
        description: apiErrorMessage(error, 'Please try again'),
        variant: 'destructive',
      });
    }
  };

  const handleArchiveListInstead = async () => {
    if (!deleteConfirmList) return;
    try {
      await archiveList.mutateAsync({ id: deleteConfirmList.id, projectId: project.id });
      toast({ title: 'List archived' });
      setDeleteConfirmList(null);
      setListDeleteConfirmed(false);
    } catch (error: unknown) {
      toast({
        title: 'Failed to archive list',
        description: apiErrorMessage(error, 'Please try again'),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteConfirmProject || !projectDeleteConfirmed) return;
    try {
      await deleteProject.mutateAsync(deleteConfirmProject.id);
      toast({ title: 'Project deleted permanently' });
      setDeleteConfirmProject(null);
      setProjectDeleteConfirmed(false);
      navigate('/projects');
    } catch (error: unknown) {
      toast({
        title: 'Failed to delete project',
        description: apiErrorMessage(error, 'Please try again'),
        variant: 'destructive',
      });
    }
  };

  const handleArchiveProjectInstead = async () => {
    if (!deleteConfirmProject) return;
    try {
      await archiveProject.mutateAsync(deleteConfirmProject.id);
      toast({ title: 'Project archived' });
      setDeleteConfirmProject(null);
      setProjectDeleteConfirmed(false);
      navigate('/projects');
    } catch (error: unknown) {
      toast({
        title: 'Failed to archive project',
        description: apiErrorMessage(error, 'Please try again'),
        variant: 'destructive',
      });
    }
  };

  const handleArchiveProject = async () => {
    if (!archiveConfirmProject) return;
    try {
      await archiveProject.mutateAsync(archiveConfirmProject.id);
      toast({ title: 'Project archived' });
      setArchiveConfirmProject(null);
    } catch (error: unknown) {
      toast({
        title: 'Failed to archive project',
        description: apiErrorMessage(error, 'Please try again'),
        variant: 'destructive',
      });
    }
  };

  const handleUnarchiveProject = async () => {
    if (!archiveConfirmProject) return;
    try {
      await unarchiveProject.mutateAsync(archiveConfirmProject.id);
      toast({ title: 'Project restored' });
      setArchiveConfirmProject(null);
    } catch (error: unknown) {
      toast({
        title: 'Failed to restore project',
        description: apiErrorMessage(error, 'Please try again'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div>
      {/* Project row */}
      <div
        className={cn(
          'group flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors cursor-pointer',
          isActive && !currentListId
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground'
        )}
      >
        {/* Expand/collapse chevron */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          className="p-0.5 hover:bg-accent rounded"
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              !isExpanded && '-rotate-90'
            )}
          />
        </button>

        {/* Project link */}
        <Link
          to={`/projects/${project.id}`}
          className={cn(
            'flex items-center gap-2 flex-1 min-w-0',
            project.isArchived && 'opacity-70'
          )}
        >
          {(() => {
            const iconDef = project.icon ? PROJECT_ICONS.find(i => i.name === project.icon) : null;
            if (iconDef) {
              const IconComponent = iconDef.icon;
              return (
                <div
                  className="h-5 w-5 rounded shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: project.color }}
                >
                  <IconComponent className="h-3 w-3 text-white" />
                </div>
              );
            }
            return (
              <div
                className="h-3 w-3 rounded-sm shrink-0"
                style={{ backgroundColor: project.color }}
              />
            );
          })()}
          <span className="truncate text-sm">{project.name}</span>
          {project.isArchived && (
            <Archive className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </Link>

        {/* Add list / menu button (internal users only, when any project action allowed) */}
        {!isClient && (canCreateList || canManageProjectMembers || canManageProjectStatuses || canArchiveProject || canUnarchiveProject || canDeleteProject) && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity',
                  menuOpen && 'opacity-100'
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => {
                setSettingsDialogOpen(true);
                setMenuOpen(false);
              }}>
                <Settings className="h-4 w-4 mr-2" />
                Project settings
              </DropdownMenuItem>
              {canCreateList && (
                <DropdownMenuItem onClick={onAddList}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add list
                </DropdownMenuItem>
              )}
              {canManageProjectMembers && (
                <DropdownMenuItem onClick={() => {
                  setMembershipDialogOpen(true);
                  setMenuOpen(false);
                }}>
                  <Users className="h-4 w-4 mr-2" />
                  Manage access
                </DropdownMenuItem>
              )}
              {canManageProjectStatuses && (
                <DropdownMenuItem onClick={() => {
                  setStatusDialogOpen(true);
                  setMenuOpen(false);
                }}>
                  <Palette className="h-4 w-4 mr-2" />
                  Manage statuses
                </DropdownMenuItem>
              )}
              {canManageProjectCustomFields && (
                <DropdownMenuItem onClick={() => {
                  setCustomFieldsDialogOpen(true);
                  setMenuOpen(false);
                }}>
                  <List className="h-4 w-4 mr-2" />
                  Manage custom fields
                </DropdownMenuItem>
              )}
              {canArchiveProject && !project.isArchived && (
                <DropdownMenuItem
                  onClick={() => {
                    setArchiveConfirmProject(project);
                    setMenuOpen(false);
                  }}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archive project
                </DropdownMenuItem>
              )}
              {canUnarchiveProject && project.isArchived && (
                <DropdownMenuItem
                  onClick={() => {
                    setArchiveConfirmProject(project);
                    setMenuOpen(false);
                  }}
                >
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                  Unarchive project
                </DropdownMenuItem>
              )}
              {canDeleteProject && !project.isArchived && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => {
                    setDeleteConfirmProject(project);
                    setProjectDeleteConfirmed(false);
                    setMenuOpen(false);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete project
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Project settings dialog */}
      {settingsDialogOpen && (
        <ProjectSettingsDialog
          project={project}
          open={settingsDialogOpen}
          onOpenChange={setSettingsDialogOpen}
        />
      )}

      {/* Project statuses dialog */}
      {statusDialogOpen && (
        <ProjectStatusesDialog
          projectId={project.id}
          projectName={project.name}
          open={statusDialogOpen}
          onOpenChange={setStatusDialogOpen}
        />
      )}

      {/* Project membership dialog */}
      {membershipDialogOpen && (
        <ProjectMembershipDialog
          projectId={project.id}
          projectName={project.name}
          open={membershipDialogOpen}
          onOpenChange={setMembershipDialogOpen}
        />
      )}

      {/* Project custom fields dialog */}
      <ManageCustomFieldsDialog
        open={customFieldsDialogOpen}
        onOpenChange={setCustomFieldsDialogOpen}
        scope="project"
        projectId={project.id}
        title={`Custom fields for ${project.name}`}
      />

      {/* Lists under this project */}
      {isExpanded && lists.length > 0 && (
        <div className="ml-4 mt-0.5 space-y-0.5">
          {lists.map((list) => (
            <ListItem
              key={list.id}
              projectId={project.id}
              projectName={project.name}
              list={list}
              capabilities={capabilities}
              isActive={currentListId === list.id}
              isClient={isClient}
              onDelete={() => {
                setDeleteConfirmList(list);
                setListDeleteConfirmed(false);
              }}
            />
          ))}
        </div>
      )}

      {/* Delete list confirmation (destructive, with archive alternative) */}
      <AlertDialog
        open={!!deleteConfirmList}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirmList(null);
            setListDeleteConfirmed(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete list permanently?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This will permanently delete &quot;{deleteConfirmList?.name}&quot; and all of its tasks, comments, attachments, and data. This cannot be undone.
                </p>
                <p className="text-muted-foreground text-sm">
                  Consider archiving instead if you might need the list later.
                </p>
                <div className="flex items-center gap-2 pt-2">
                  <Checkbox
                    id="list-delete-confirm"
                    checked={listDeleteConfirmed}
                    onCheckedChange={(checked) => setListDeleteConfirmed(checked === true)}
                  />
                  <label htmlFor="list-delete-confirm" className="text-sm font-medium cursor-pointer">
                    I understand this action is permanent
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="order-2 sm:order-1"
              onClick={handleArchiveListInstead}
              disabled={archiveList.isPending || !canArchiveList}
            >
              {archiveList.isPending ? 'Archiving...' : 'Archive instead'}
            </Button>
            <AlertDialogCancel className="order-3">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteList}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 order-1 sm:order-4"
              disabled={deleteList.isPending || !listDeleteConfirmed}
            >
              {deleteList.isPending ? 'Deleting...' : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive project confirmation */}
      <AlertDialog open={!!archiveConfirmProject} onOpenChange={(open) => !open && setArchiveConfirmProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archiveConfirmProject?.isArchived ? 'Unarchive project?' : 'Archive project?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {archiveConfirmProject?.isArchived
                ? `Restore "${archiveConfirmProject?.name}" so it appears in the main project list again.`
                : `Archive "${archiveConfirmProject?.name}"? It will be hidden from the main list but can be restored later.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={archiveConfirmProject?.isArchived ? handleUnarchiveProject : handleArchiveProject}
              disabled={archiveProject.isPending || unarchiveProject.isPending}
            >
              {archiveConfirmProject?.isArchived
                ? (unarchiveProject.isPending ? 'Restoring...' : 'Unarchive')
                : (archiveProject.isPending ? 'Archiving...' : 'Archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete project confirmation (destructive, with archive alternative) */}
      <AlertDialog
        open={!!deleteConfirmProject}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirmProject(null);
            setProjectDeleteConfirmed(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete project permanently?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This will permanently delete &quot;{deleteConfirmProject?.name}&quot; and all of its lists, tasks, comments, attachments, chats, and data. This cannot be undone.
                </p>
                <p className="text-muted-foreground text-sm">
                  Consider archiving instead if you might need the project later.
                </p>
                <div className="flex items-center gap-2 pt-2">
                  <Checkbox
                    id="project-delete-confirm"
                    checked={projectDeleteConfirmed}
                    onCheckedChange={(checked) => setProjectDeleteConfirmed(checked === true)}
                  />
                  <label htmlFor="project-delete-confirm" className="text-sm font-medium cursor-pointer">
                    I understand this action is permanent
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="order-2 sm:order-1"
              onClick={handleArchiveProjectInstead}
              disabled={archiveProject.isPending || !canArchiveProject}
            >
              {archiveProject.isPending ? 'Archiving...' : 'Archive instead'}
            </Button>
            <AlertDialogCancel className="order-3">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 order-1 sm:order-4"
              disabled={deleteProject.isPending || !projectDeleteConfirmed}
            >
              {deleteProject.isPending ? 'Deleting...' : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface ListItemProps {
  projectId: string;
  projectName: string;
  list: ListType;
  capabilities: CapabilityMap | undefined;
  isActive: boolean;
  isClient: boolean;
  onDelete: () => void;
}

function ListItem({ projectId, projectName, list, capabilities, isActive, isClient, onDelete }: ListItemProps) {
  const canManageListTagSets = Boolean(capabilities?.['list.tag_sets.assign'] || capabilities?.['list.tag_sets.remove']);
  const canArchiveList = Boolean(capabilities?.['list.archive']);
  const canUnarchiveList = Boolean(capabilities?.['list.unarchive']);
  const canDeleteList = Boolean(capabilities?.['list.delete']);
  const canManageListMembers = Boolean(capabilities?.['list.members.manage']);
  const canManageListStatuses = Boolean(capabilities?.['status.list.manage']);
  const canManageListCustomFields = Boolean(capabilities?.['custom_fields.list.manage']);
  const [customFieldsDialogOpen, setCustomFieldsDialogOpen] = useState(false);
  const archiveList = useArchiveList();
  const unarchiveList = useUnarchiveList();
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [membershipDialogOpen, setMembershipDialogOpen] = useState(false);
  const [tagSetsDialogOpen, setTagSetsDialogOpen] = useState(false);
  const [archiveConfirmList, setArchiveConfirmList] = useState<{ list: ListType; unarchive: boolean } | null>(null);

  const handleArchiveList = async () => {
    if (!archiveConfirmList) return;
    try {
      if (archiveConfirmList.unarchive) {
        await unarchiveList.mutateAsync({ id: archiveConfirmList.list.id, projectId });
      } else {
        await archiveList.mutateAsync({ id: archiveConfirmList.list.id, projectId });
      }
      toast({ title: archiveConfirmList.unarchive ? 'List restored' : 'List archived' });
      setArchiveConfirmList(null);
    } catch (error: unknown) {
      toast({
        title: archiveConfirmList.unarchive ? 'Failed to restore list' : 'Failed to archive list',
        description: apiErrorMessage(error, 'Please try again'),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <div
        className={cn(
          'group flex items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors',
          isActive
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground',
          list.isArchived && 'opacity-70'
        )}
      >
        <List className="h-4 w-4 shrink-0" />

        <Link
          to={`/projects/${projectId}/lists/${list.id}`}
          className="flex-1 truncate flex items-center gap-1"
        >
          {list.name}
          {list.isArchived && <Archive className="h-3 w-3 shrink-0 text-muted-foreground" />}
        </Link>

        {/* Custom statuses indicator */}
        {list.usesCustomStatuses && (
          <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" title="Custom statuses" />
        )}

        {/* Task count - show activeTaskCount (excludes done tasks) like ClickUp */}
        {list.activeTaskCount !== undefined && (
          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
            {list.activeTaskCount}
          </span>
        )}

        {/* Menu for internal users (when any list action allowed) */}
        {!isClient && (canManageListMembers || canManageListStatuses || canManageListTagSets || canArchiveList || canUnarchiveList || canDeleteList) && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity',
                  menuOpen && 'opacity-100'
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {canManageListMembers && (
                <DropdownMenuItem onClick={() => {
                  setMembershipDialogOpen(true);
                  setMenuOpen(false);
                }}>
                  <Users className="h-4 w-4 mr-2" />
                  Manage access
                </DropdownMenuItem>
              )}
              {canManageListStatuses && (
                <DropdownMenuItem onClick={() => {
                  setStatusDialogOpen(true);
                  setMenuOpen(false);
                }}>
                  <Palette className="h-4 w-4 mr-2" />
                  Manage statuses
                </DropdownMenuItem>
              )}
              {canManageListTagSets && (
                <DropdownMenuItem onClick={() => {
                  setTagSetsDialogOpen(true);
                  setMenuOpen(false);
                }}>
                  <Tags className="h-4 w-4 mr-2" />
                  Manage tags
                </DropdownMenuItem>
              )}
              {canManageListCustomFields && (
                <DropdownMenuItem onClick={() => {
                  setCustomFieldsDialogOpen(true);
                  setMenuOpen(false);
                }}>
                  <List className="h-4 w-4 mr-2" />
                  Manage custom fields
                </DropdownMenuItem>
              )}
              {canArchiveList && !list.isArchived && (
                <DropdownMenuItem
                  onClick={() => {
                    setArchiveConfirmList({ list, unarchive: false });
                    setMenuOpen(false);
                  }}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archive list
                </DropdownMenuItem>
              )}
              {canUnarchiveList && list.isArchived && (
                <DropdownMenuItem
                  onClick={() => {
                    setArchiveConfirmList({ list, unarchive: true });
                    setMenuOpen(false);
                  }}
                >
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                  Unarchive list
                </DropdownMenuItem>
              )}
              {canDeleteList && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                    setMenuOpen(false);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* List custom fields dialog */}
      <ManageCustomFieldsDialog
        open={customFieldsDialogOpen}
        onOpenChange={setCustomFieldsDialogOpen}
        scope="list"
        listId={list.id}
        title={`Custom fields for ${list.name}`}
      />

      {/* List archive confirmation */}
      <AlertDialog open={!!archiveConfirmList} onOpenChange={(open) => !open && setArchiveConfirmList(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archiveConfirmList?.unarchive ? 'Unarchive list?' : 'Archive list?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {archiveConfirmList?.unarchive
                ? `Restore "${archiveConfirmList?.list.name}" so it appears in the list again.`
                : `Archive "${archiveConfirmList?.list.name}"? It will be hidden but can be restored later.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveList}
              disabled={archiveList.isPending || unarchiveList.isPending}
            >
              {archiveConfirmList?.unarchive
                ? (unarchiveList.isPending ? 'Restoring...' : 'Unarchive')
                : (archiveList.isPending ? 'Archiving...' : 'Archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* List statuses dialog */}
      {statusDialogOpen && (
        <ListStatusesDialog
          list={list}
          projectName={projectName}
          open={statusDialogOpen}
          onOpenChange={setStatusDialogOpen}
        />
      )}

      {/* List membership dialog */}
      {membershipDialogOpen && (
        <ListMembershipDialog
          listId={list.id}
          listName={list.name}
          open={membershipDialogOpen}
          onOpenChange={setMembershipDialogOpen}
        />
      )}

      {/* List tag sets dialog */}
      {tagSetsDialogOpen && (
        <ListTagSetsDialog
          listId={list.id}
          listName={list.name}
          open={tagSetsDialogOpen}
          onOpenChange={setTagSetsDialogOpen}
        />
      )}
    </>
  );
}
